import { UserGridPreset } from "@q-food/q-food";

/**
 * Marker that identifies clipboard text as a grid profile. Paste refuses anything without it, so
 * an accidental Ctrl+V of unrelated text fails with a message instead of creating a broken profile.
 */
export const GRID_PRESET_CLIPBOARD_TYPE = "q-food/grid-preset";

export interface GridPresetClipboardRole {
	id: string;
	name: string;
}

export interface GridPresetClipboardPayload {
	type: typeof GRID_PRESET_CLIPBOARD_TYPE;
	version: 1;
	gridKey: string;
	/** ag-grid `ColumnState[]` JSON, verbatim from the preset. */
	columnState: string;
	/** Filter model JSON, verbatim from the preset — `$today` sentinels included. */
	filterState: string;
	name: string;
	isShared: boolean;
	/**
	 * Role names travel alongside the ids purely so the paste side can name the roles it had to
	 * drop; only the ids are ever sent back to the server.
	 */
	allowedRoles: GridPresetClipboardRole[];
}

/**
 * Renders a preset as the clipboard envelope. Pretty-printed on purpose: the payload is meant to
 * survive a trip through a chat message or a text file, where a single long line gets mangled.
 */
export function serializeGridPreset(preset: UserGridPreset): string {
	const payload: GridPresetClipboardPayload = {
		type: GRID_PRESET_CLIPBOARD_TYPE,
		version: 1,
		gridKey: preset.gridKey,
		columnState: preset.columnState,
		filterState: preset.filterState,
		name: preset.name,
		isShared: preset.isShared ?? false,
		allowedRoles: (preset.allowedRoles ?? []).map((role) => ({ id: role.id, name: role.name })),
	};

	return JSON.stringify(payload, null, 2);
}

function isNonEmptyString(value: unknown): value is string {
	return typeof value === "string" && value.trim() !== "";
}

function isJson(value: string): boolean {
	try {
		JSON.parse(value);
		return true;
	} catch {
		return false;
	}
}

/**
 * Parses and validates clipboard text. Returns `null` for anything that is not a grid profile of a
 * version we understand — never throws, so callers can treat `null` as "show the user a message".
 */
export function parseGridPreset(text: string | null | undefined): GridPresetClipboardPayload | null {
	if (!isNonEmptyString(text)) return null;

	let parsed: unknown;
	try {
		parsed = JSON.parse(text);
	} catch {
		return null;
	}

	if (typeof parsed !== "object" || parsed === null) return null;

	const candidate = parsed as Partial<GridPresetClipboardPayload>;

	if (candidate.type !== GRID_PRESET_CLIPBOARD_TYPE) return null;
	if (candidate.version !== 1) return null;
	if (!isNonEmptyString(candidate.gridKey)) return null;
	if (!isNonEmptyString(candidate.name)) return null;
	if (!isNonEmptyString(candidate.columnState) || !isJson(candidate.columnState)) return null;
	if (!isNonEmptyString(candidate.filterState) || !isJson(candidate.filterState)) return null;

	const allowedRoles = Array.isArray(candidate.allowedRoles)
		? candidate.allowedRoles
				.filter((role): role is GridPresetClipboardRole => !!role && isNonEmptyString(role.id))
				.map((role) => ({ id: role.id, name: isNonEmptyString(role.name) ? role.name : role.id }))
		: [];

	return {
		type: GRID_PRESET_CLIPBOARD_TYPE,
		version: 1,
		gridKey: candidate.gridKey,
		columnState: candidate.columnState,
		filterState: candidate.filterState,
		name: candidate.name,
		isShared: candidate.isShared === true,
		allowedRoles,
	};
}

/**
 * Reads the clipboard, returning `null` instead of throwing when that is not possible.
 *
 * `writeText` is available almost everywhere, but `readText` is not: it needs a secure context and
 * Firefox does not expose it to page scripts at all. Callers must therefore have a manual-paste
 * fallback rather than a button that silently does nothing.
 */
export async function readClipboardText(): Promise<string | null> {
	try {
		const text = await navigator.clipboard?.readText();
		return isNonEmptyString(text) ? text : null;
	} catch {
		return null;
	}
}
